import React from "react";
import { TextField, type TextFieldState } from "../TextField/TextField";

// ─── Shared text style ────────────────────────────────────────────────────────

const TEXT_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
};

// ─── Warning Icon ─────────────────────────────────────────────────────────────

const WarningIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 6.5V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className" | "style"> {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  leadingBadge?: string;
  showCounter?: boolean;
  maxLength?: number;
  style?: React.CSSProperties;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FormField({
  label,
  description,
  error,
  required,
  leadingIcon,
  trailingIcon,
  leadingBadge,
  showCounter = false,
  maxLength,
  disabled,
  placeholder,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  id,
  name,
  type = "text",
  style,
  ...rest
}: FormFieldProps) {
  const uidRef = React.useRef(`makula-field-${Math.random().toString(36).slice(2)}`);
  const inputId = id ?? uidRef.current;

  const [focused, setFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(
    defaultValue !== undefined ? String(defaultValue) : ""
  );

  const isControlled = value !== undefined;
  const currentValue = isControlled ? String(value ?? "") : internalValue;

  const fieldState: TextFieldState = disabled
    ? "disabled"
    : error
    ? "error"
    : focused
    ? "focused"
    : currentValue
    ? "filled"
    : "default";

  const charCount = currentValue.length;
  const isError = fieldState === "error";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-s)",
        width: "100%",
        ...style,
      }}
    >
      {/* Placeholder colour requires a scoped style tag */}
      <style>{`#${CSS.escape(inputId)}::placeholder { color: var(--color-content-secondary); }`}</style>

      {label && (
        <label
          htmlFor={inputId}
          style={{
            ...TEXT_STYLE,
            display: "flex",
            gap: "var(--spacing-2xs)",
            alignItems: "center",
            color: "var(--color-content-secondary)",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "default",
          }}
        >
          <span>{label}</span>
          {required && <span aria-hidden="true">*</span>}
        </label>
      )}

      <TextField
        id={inputId}
        state={fieldState}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        leadingBadge={leadingBadge}
        disabled={disabled}
        placeholder={placeholder}
        value={isControlled ? value : internalValue}
        maxLength={maxLength}
        name={name}
        type={type}
        onChange={(e) => {
          if (!isControlled) setInternalValue(e.target.value);
          onChange?.(e);
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        {...rest}
      />

      {/* Error row */}
      {isError && error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--spacing-xs)",
            paddingInline: "var(--spacing-2xs)",
            color: "var(--color-content-danger)",
          }}
        >
          <WarningIcon />
          <div style={{ display: "flex", flex: 1, justifyContent: "space-between", gap: "var(--spacing-s)" }}>
            <span style={{ ...TEXT_STYLE, flex: 1, color: "var(--color-content-danger)" }}>
              {error}
            </span>
            {showCounter && maxLength !== undefined && (
              <span style={{ ...TEXT_STYLE, color: "var(--color-content-danger)", textAlign: "right", whiteSpace: "nowrap", flexShrink: 0 }}>
                {charCount}/{maxLength}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Description + counter (non-error states) */}
      {!isError && (description || (showCounter && maxLength !== undefined)) && (
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--spacing-s)" }}>
          {description && (
            <span style={{ ...TEXT_STYLE, flex: 1, color: "var(--color-content-secondary)" }}>
              {description}
            </span>
          )}
          {showCounter && maxLength !== undefined && (
            <span style={{ ...TEXT_STYLE, color: "var(--color-content-secondary)", textAlign: "right", whiteSpace: "nowrap", flexShrink: 0 }}>
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
